
<template>
	<div class="container">
		<Transition name="fade">
			<PageLoaderComponent v-if="loading" />
			<div v-else>
				<div class="grid flex flex--wrap mb-24">
					<div class="grid__item a7-12 f6-6">
						<h1 class="h1">
							Add new project
						</h1>
					</div>
					<div class="grid__item a5-12 f6-6">
						<div class="notice-bar notice-bar--header">
							<p>
								<b>The status of this project is currently pending.</b><br>
								A project number will be assigned once it is ready to go live
							</p>
						</div>
					</div>
				</div>
				<div class="new-project-by-api">
					<div class="flex flex--wrap flex--gap-large flex--justify-space-between flex--align-center">
						<div>
							<p><b>Please link this new project to the PandaDoc fee proposal.</b></p>
							<p class="iota">
								Project title and scope will be pre-filled to save time.
							</p>
						</div>
						<form
							class="new-project-by-api__form"
							@submit.prevent="makePandadocRequest"
						>
							<label
								class="form__label form__label--small"
								for="api-id"
							>
								PandaDoc document ID (final part of URL, e.g. bT7ypof54GRE76)
							</label>
							<div class="flex flex--align-start flex--wrap flex--gap-medium">
								<div>
									<input
										id="api-id"
										v-model="form_fields.pandadoc_id"
										class="form__input form__input--thin"
										type="text"
									>
									<p
										v-if="form_errors.pandadoc_id"
										class="form__error"
									>
										{{ form_errors.pandadoc_id }}
									</p>
								</div>
								<button
									class="button button--small"
									:class="{ 'button--loading': loading_pandadoc_request }"
									type="submit"
									:disabled="loading_pandadoc_request"
								>
									{{ loading_pandadoc_request ? 'Fetching...' : pandadoc_info_fetched ? 'Information fetched!' : 'Fetch this information' }}
								</button>
							</div>
						</form>
					</div>
				</div>
				<div class="steps-nav">
					<div
						v-for="( step, index ) in steps"
						:key="'steps-nav-' + index"
						class="steps-nav__step"
						:class="( step.step_number <= active_step_number ) ? ' steps-nav__step--active' : null"
					>
						<p
							class="steps-nav__step-title"
							:class="( step.step_number < active_step_number ) ? ' steps-nav__step-title--completed' : null"
						>
							Step {{ step.step_number }}
						</p>
						<p>{{ step.title }}</p>
					</div>
				</div>
				<div class="grid flex flex--wrap mb-24">
					<div class="grid__item a7-12 d6-6">
						<div class="steps">
							<ProjectDetailsComponent
								:step="steps[0]"
								:active-step-number="active_step_number"
							/>
							<ClientInformationComponent
								:step="steps[1]"
								:active-step-number="active_step_number"
							/>
							<ExternalTeamComponent
								:step="steps[2]"
								:active-step-number="active_step_number"
							/>
							<InternalTeamComponent
								:step="steps[3]"
								:active-step-number="active_step_number"
							/>
							<ProjectScopeComponent
								:step="steps[4]"
								:active-step-number="active_step_number"
							/>
							<ReviewComponent
								:step="steps[5]"
								:active-step-number="active_step_number"
							/>
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script setup>
import PageLoaderComponent from '../components/PageLoaderComponent';
import ClientInformationComponent from '../components/new-project/ClientInformationComponent';
import ProjectDetailsComponent from '../components/new-project/ProjectDetailsComponent';
import ExternalTeamComponent from '../components/new-project/ExternalTeamComponent';
import InternalTeamComponent from '../components/new-project/InternalTeamComponent';
import ProjectScopeComponent from '../components/new-project/ProjectScopeComponent';
import ReviewComponent from '../components/new-project/ReviewComponent';

import { USER_GROUP_CLIENT } from '../constants';

import { useDataStore } from '../stores/data';
import { useNewProjectStore } from '../stores/new-project.js';
import { useRouter } from 'vue-router';
import { ref, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';

const data_store = useDataStore();
const new_project_store = useNewProjectStore();
const { setBreadcrumbs } = data_store;
const { getPandaDocInfo } = new_project_store;
const {
	form_fields,
	form_errors,
	steps,
	active_step_number
} = storeToRefs( new_project_store );
const router = useRouter();


const { user_group } = storeToRefs( data_store );

const user_is_client = computed( () => {
	return user_group.value === USER_GROUP_CLIENT;
} );

const loading = ref( true );

// Mount
onMounted( async() => {
	loading.value = true;

	if ( user_is_client.value ) {
		router.push( { name: 'home' } );
		return;
	}

	setBreadcrumbs( [
		{
			label: 'Projects',
			route: { name: 'projects' },
			icon: 'briefcase'
		},
		{
			label: 'Add new project'
		}
	] );

	form_errors.value.pandadoc_id = null;

	loading.value = false;
} );

const loading_pandadoc_request = ref( false );
const pandadoc_info_fetched = ref( false );

async function makePandadocRequest() {
	loading_pandadoc_request.value = true;

	pandadoc_info_fetched.value = await getPandaDocInfo();

	loading_pandadoc_request.value = false;
}
</script>
